import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MarketOverviewSettings from './MarketOverviewSettings'
import {
  CHART_TYPE,
  getCountryName,
  KEY,
  OVERVIEW_TAB_TYPE,
  TYPE
} from './MarketOverviewTable/MarketOverviewTable.settings'
import { useDSmarketOverviewData } from '../../../store/dataStore'
import { useGlobalStoreData } from '../../../store/globalStore'
import { useSDSsearchParams, useSearchDataStoreActions } from '../../../store/searchDataStore'
import { MarketCalculusCard } from '../../../components/preview/Preview'
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap'
import classnames from 'classnames'

const ValueSection = forwardRef((props, ref) => {
  const { t } = useTranslation()
  const countriesGSD = useGlobalStoreData.countries()

  const { searchTrigger } = useSearchDataStoreActions()
  const searchParams = useSDSsearchParams()

  const marketOverviewData = useDSmarketOverviewData()
  const [activeAltTab, setActiveAltTab] = useState(OVERVIEW_TAB_TYPE.SOURCE)
  const importer = marketOverviewData?.value?.importer
  const exporter = marketOverviewData?.value?.exporter
  const destinationCountry = marketOverviewData?.value?.destinationCountry
  const sourceCountry = marketOverviewData?.value?.sourceCountry
  const world = marketOverviewData?.value?.world
  const toggleAltTab = (altTab) => {
    if (activeAltTab !== altTab) setActiveAltTab(altTab)
  }

  const importerCountryYearlyRef = useRef(null)
  const importerCountryMonthlyRef = useRef(null)
  const destinationCountryCountryYearlyRef = useRef(null)
  const destinationCountryCountryMonthlyRef = useRef(null)
  const exporterCountryYearlyRef = useRef(null)
  const exporterCountryMonthlyRef = useRef(null)
  const sourceCountryCountryYearlyRef = useRef(null)
  const sourceCountryCountryMonthlyRef = useRef(null)
  const worldImporterCountryYearlyRef = useRef(null)
  const worldImporterCountryMonthlyRef = useRef(null)
  const worldExporterCountryYearlyRef = useRef(null)
  const worldExporterCountryMonthlyRef = useRef(null)

  useImperativeHandle(ref, () => ({
    generateTablesPdf () {
      return [
        ...importerCountryYearlyRef.current.generateTablePdf('importerCountryYearlyVal'),
        ...importerCountryMonthlyRef.current.generateTablePdf('importerCountryMonthlyVal'),
        ...destinationCountryCountryYearlyRef.current.generateTablePdf('destinationCountryCountryYearlyVal'),
        ...destinationCountryCountryMonthlyRef.current.generateTablePdf('destinationCountryCountryMonthlyVal'),

        ...exporterCountryYearlyRef.current.generateTablePdf('exporterCountryYearlyVal'),
        ...exporterCountryMonthlyRef.current.generateTablePdf('exporterCountryMonthlyVal'),
        ...sourceCountryCountryYearlyRef.current.generateTablePdf('sourceCountryCountryYearlyVal'),
        ...sourceCountryCountryMonthlyRef.current.generateTablePdf('sourceCountryCountryMonthlyVal'),

        ...worldImporterCountryYearlyRef.current.generateTablePdf('worldImporterCountryYearlyVal'),
        ...worldImporterCountryMonthlyRef.current.generateTablePdf('worldImporterCountryMonthlyVal'),
        ...worldExporterCountryYearlyRef.current.generateTablePdf('worldExporterCountryYearlyVal'),
        ...worldExporterCountryMonthlyRef.current.generateTablePdf('worldExporterCountryMonthlyVal')
      ]
    }
  }))

  return (
    <>
      <MarketCalculusCard className={props.null || searchTrigger ? 'loading-div' : 'mt-3'}>
        <Nav tabs className="nav-tabs-s2">
          <NavItem>
            <NavLink
              tag="a"
              href="#tab"
              className={classnames('btn btn-sm', { 'btn-primary': activeAltTab === OVERVIEW_TAB_TYPE.SOURCE })}
              onClick={(ev) => {
                ev.preventDefault()
                toggleAltTab(OVERVIEW_TAB_TYPE.SOURCE)
              }}
            >
              {getCountryName(searchParams.countryToExport, countriesGSD, 'country_code')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              tag="a"
              href="#tab"
              className={classnames('btn btn-sm', { 'btn-primary': activeAltTab === OVERVIEW_TAB_TYPE.DESTINATION })}
              onClick={(ev) => {
                ev.preventDefault()
                toggleAltTab(OVERVIEW_TAB_TYPE.DESTINATION)
              }}
            >
              {getCountryName(searchParams.yourCountry, countriesGSD, 'country_code')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              tag="a"
              href="#tab"
              className={classnames('btn btn-sm', { 'btn-primary': activeAltTab === OVERVIEW_TAB_TYPE.WORLD })}
              onClick={(ev) => {
                ev.preventDefault()
                toggleAltTab(OVERVIEW_TAB_TYPE.WORLD)
              }}
            >
              {t('world')}
            </NavLink>
          </NavItem>
        </Nav>
      </MarketCalculusCard>

      <TabContent activeTab={activeAltTab}>
        <TabPane tabId={OVERVIEW_TAB_TYPE.SOURCE}>
          <MarketOverviewSettings
            ref={importerCountryYearlyRef}
            type={TYPE.IMPORTER}
            responseCountry={countriesGSD}
            timeTags={importer?.time?.yearly}
            countryValues={importer?.country?.yearly}
            removeNullCols={importer?.country?.yearly ? importer?.country?.yearly[0]?.valuesByTimeTag[0] == null : false}
            title={KEY.YEARLY}
            yTitle={t('chart_value_yTitle')}
            chartData={importer?.data?.yearly}
            chartType={CHART_TYPE.BAR}
            chartId="MultipleBarChart-01"
            tableType={'value'}
            tableCode="T-PG-01"
            graphicCode="G-PG-01"
            tabType={OVERVIEW_TAB_TYPE.SOURCE}
            footer={marketOverviewData?.value?.importerYearlyExplanation}
          />
          <MarketOverviewSettings
            ref={importerCountryMonthlyRef}
            type={TYPE.IMPORTER}
            responseCountry={countriesGSD}
            timeTags={importer?.time?.monthly}
            countryValues={importer?.country?.monthly}
            title={KEY.MONTHLY}
            yTitle={t('chart_value_yTitle')}
            chartData={importer?.data?.monthly}
            chartType={CHART_TYPE.LINE}
            chartId="MultipleLineChart-02"
            tableType={'value'}
            tableCode="T-PG-02"
            graphicCode="G-PG-02"
            tabType={OVERVIEW_TAB_TYPE.SOURCE}
            footer={marketOverviewData?.value?.importerMonthlyExplanation}
          />
          <MarketOverviewSettings
            ref={destinationCountryCountryYearlyRef}
            type={TYPE.EXPORTER}
            responseCountry={countriesGSD}
            timeTags={destinationCountry?.time?.yearly}
            countryValues={destinationCountry?.country?.yearly}
            title={KEY.YEARLY}
            yTitle={t('chart_value_yTitle')}
            chartData={destinationCountry?.data?.yearly}
            chartType={CHART_TYPE.BAR}
            chartId="MultipleBarChart-DestinationCountry01"
            tableType={'value'}
            tableCode="T-PG-03"
            graphicCode="G-PG-03"
            tabType={OVERVIEW_TAB_TYPE.SOURCE}
            footer={marketOverviewData?.value?.destinationCountryAsExporterYearlyExplanation}
          />
          <MarketOverviewSettings
            ref={destinationCountryCountryMonthlyRef}
            type={TYPE.EXPORTER}
            responseCountry={countriesGSD}
            timeTags={destinationCountry?.time?.monthly}
            countryValues={destinationCountry?.country?.monthly}
            title={KEY.MONTHLY}
            yTitle={t('chart_value_yTitle')}
            chartData={destinationCountry?.data?.monthly}
            chartType={CHART_TYPE.LINE}
            chartId="MultipleBarChart-DestinationCountry02"
            tableType={'value'}
            tableCode="T-PG-04"
            graphicCode="G-PG-04"
            tabType={OVERVIEW_TAB_TYPE.SOURCE}
            footer={marketOverviewData?.value?.destinationCountryAsExporterMonthlyExplanation}
          />
        </TabPane>
        <TabPane tabId={OVERVIEW_TAB_TYPE.DESTINATION}>
          <MarketOverviewSettings
            ref={exporterCountryYearlyRef}
            type={TYPE.EXPORTER}
            responseCountry={countriesGSD}
            timeTags={exporter?.time?.yearly}
            countryValues={exporter?.country?.yearly}
            title={KEY.YEARLY}
            yTitle={t('chart_value_yTitle')}
            chartData={exporter?.data?.yearly}
            chartType={CHART_TYPE.BAR}
            chartId="MultipleBarChart-03"
            tableType={'value'}
            tableCode="T-PG-05"
            graphicCode="G-PG-05"
            tabType={OVERVIEW_TAB_TYPE.DESTINATION}
            footer={marketOverviewData?.value?.exporterYearlyExplanation}
          />
          <MarketOverviewSettings
            ref={exporterCountryMonthlyRef}
            type={TYPE.EXPORTER}
            responseCountry={countriesGSD}
            timeTags={exporter?.time?.monthly}
            countryValues={exporter?.country?.monthly}
            title={KEY.MONTHLY}
            yTitle={t('chart_value_yTitle')}
            chartData={exporter?.data?.monthly}
            chartType={CHART_TYPE.LINE}
            chartId="MultipleLineChart-04"
            tableType={'value'}
            tableCode="T-PG-06"
            graphicCode="G-PG-06"
            tabType={OVERVIEW_TAB_TYPE.DESTINATION}
            footer={marketOverviewData?.value?.exporterMonthlyExplanation}
          />
          <MarketOverviewSettings
            ref={sourceCountryCountryYearlyRef}
            type={TYPE.IMPORTER}
            responseCountry={countriesGSD}
            timeTags={sourceCountry?.time?.yearly}
            countryValues={sourceCountry?.country?.yearly}
            title={KEY.YEARLY}
            yTitle={t('chart_value_yTitle')}
            chartData={sourceCountry?.data?.yearly}
            chartType={CHART_TYPE.BAR}
            chartId="MultipleBarChart-SourceCountry"
            tableType={'value'}
            tableCode="T-PG-07"
            graphicCode="G-PG-07"
            tabType={OVERVIEW_TAB_TYPE.DESTINATION}
            footer={marketOverviewData?.value?.sourceCountryAsImporterYearlyExplanation}
          />
          <MarketOverviewSettings
            ref={sourceCountryCountryMonthlyRef}
            type={TYPE.IMPORTER}
            responseCountry={countriesGSD}
            timeTags={sourceCountry?.time?.monthly}
            countryValues={sourceCountry?.country?.monthly}
            title={KEY.MONTHLY}
            yTitle={t('chart_value_yTitle')}
            chartData={sourceCountry?.data?.monthly}
            chartType={CHART_TYPE.LINE}
            chartId="MultipleBarChart-DestinationCountry"
            tableType={'value'}
            tableCode="T-PG-08"
            graphicCode="G-PG-08"
            tabType={OVERVIEW_TAB_TYPE.DESTINATION}
            footer={marketOverviewData?.value?.sourceCountryAsImporterMonthlyExplanation}
          />
        </TabPane>
        <TabPane tabId={OVERVIEW_TAB_TYPE.WORLD}>
          <MarketOverviewSettings
            ref={worldImporterCountryYearlyRef}
            type={TYPE.IMPORTER}
            responseCountry={countriesGSD}
            timeTags={world?.importer.time?.yearly}
            countryValues={world?.importer.country?.yearly}
            title={KEY.YEARLY}
            yTitle={t('chart_value_yTitle')}
            chartData={world?.importer.data?.yearly}
            chartType={CHART_TYPE.BAR}
            chartId="ValueBarChart-09"
            tableType={'value'}
            tableCode="T-PG-09"
            graphicCode="G-PG-09"
            tabType={OVERVIEW_TAB_TYPE.WORLD}
            isWorld
            footer={marketOverviewData?.value?.worldImportYearlyExplanation}

          />
          <MarketOverviewSettings
            ref={worldImporterCountryMonthlyRef}
            type={TYPE.IMPORTER}
            responseCountry={countriesGSD}
            timeTags={world?.importer.time?.monthly}
            countryValues={world?.importer.country?.monthly}
            title={KEY.MONTHLY}
            yTitle={t('chart_value_yTitle')}
            chartData={world?.importer.data?.monthly}
            chartType={CHART_TYPE.LINE}
            chartId="ValueLineChart-10"
            tableType={'value'}
            tableCode="T-PG-10"
            graphicCode="G-PG-10"
            tabType={OVERVIEW_TAB_TYPE.WORLD}
            isWorld
            footer={marketOverviewData?.value?.worldImportMonthlyExplanation}

          />

          <MarketOverviewSettings
            ref={worldExporterCountryYearlyRef}
            type={TYPE.EXPORTER}
            responseCountry={countriesGSD}
            timeTags={world?.exporter.time?.yearly}
            countryValues={world?.exporter.country?.yearly}
            title={KEY.YEARLY}
            yTitle={t('chart_value_yTitle')}
            chartData={world?.exporter.data?.yearly}
            chartType={CHART_TYPE.BAR}
            chartId="ValueBarChart-11"
            tableType={'value'}
            tableCode="T-PG-11"
            graphicCode="G-PG-11"
            tabType={OVERVIEW_TAB_TYPE.WORLD}
            isWorld
            footer={marketOverviewData?.value?.worldExportYearlyExplanation}
          />
          <MarketOverviewSettings
            ref={worldExporterCountryMonthlyRef}
            type={TYPE.EXPORTER}
            responseCountry={countriesGSD}
            timeTags={world?.exporter.time?.monthly}
            countryValues={world?.exporter.country?.monthly}
            title={KEY.MONTHLY}
            yTitle={t('chart_value_yTitle')}
            chartData={world?.exporter.data?.monthly}
            chartType={CHART_TYPE.LINE}
            chartId="ValueLineChart-12"
            tableType={'value'}
            tableCode="T-PG-12"
            graphicCode="G-PG-12"
            tabType={OVERVIEW_TAB_TYPE.WORLD}
            isWorld
            footer={marketOverviewData?.value?.worldExportMonthlyExplanation}
          />
        </TabPane>
      </TabContent>
    </>
  )
})

export default ValueSection
